'use client';

import { Box, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { colors } from '@/styles/theme';

interface LiveBadgeProps {
  /** Optionally override dot size in px. Default: 7 */
  dotSize?: number;
  /** Optionally override font size. Default: '0.65rem' */
  fontSize?: string;
  /** Custom MUI style object */
  sx?: any;
}

/**
 * Reusable animated live badge — pulsing red dot + "LIVE" label.
 * Only renders when mounted (controlled by the parent via conditional rendering).
 */
export function LiveBadge({ dotSize = 7, fontSize = '0.9375rem', sx }: LiveBadgeProps = {}) {
  const { t } = useT();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 0.5,
        color: colors.error.main,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          bgcolor: 'currentColor',
          flexShrink: 0,
          '@keyframes livePulse': {
            '0%': { boxShadow: `0 0 0 0 currentColor` },
            '70%': { boxShadow: '0 0 0 6px transparent' },
            '100%': { boxShadow: `0 0 0 0 transparent` },
          },
          animation: 'livePulse 1.5s ease-in-out infinite',
        }}
      />
      <Typography
        sx={{
          fontSize,
          fontWeight: 700,
          color: 'inherit',
          letterSpacing: '0.06em',
          lineHeight: 1,
        }}
      >
        {t('sports.live').toUpperCase()}
      </Typography>
    </Box>
  );
}
